import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { FIND_PERSON_COMPANY_CREDITS } from "@signalist/shared-types";

import {
	faBuilding,
	faEnvelope,
	faPhone,
	faSparkles,
	faStar,
	faUserMagnifyingGlass,
	faUserPlus,
} from "@fortawesome/pro-regular-svg-icons";

import { creditMenuSubtitle } from "../CreditConfirmModal/creditMenuSubtitle";

import { FooterActionItem } from "./FooterActionItem";
import { FooterPopoverGroup } from "./FooterPopoverGroup";

interface FooterEnrichGroupProps {
	ts: (key: string) => string;
	/** True when the "Enrich" popover is the open one. */
	isOpen: boolean;
	onToggle: () => void;
	/** Close every popover. */
	onClose: () => void;
	searchForEmail?: {
		searchForEmail?: () => void;
		searchForEmailIsLoading?: boolean;
	};
	searchForPhone?: {
		searchForPhone?: () => void;
		searchForPhoneIsLoading?: boolean;
	};
	searchForCompanyEmailAndPhone?: {
		searchForCompanyEmailAndPhone?: () => void;
		searchForCompanyEmailAndPhoneIsLoading?: boolean;
	};
	leadScoring?: {
		leadScoring?: () => void;
		leadScoringIsLoading?: boolean;
	};
	deepResearch?: {
		deepResearch?: () => void;
		deepResearchIsLoading?: boolean;
	};
	findMorePeople?: {
		findMorePeople?: () => void;
		findMorePeopleIsLoading?: boolean;
	};
	bulkFindPerson?: {
		bulkFindPerson?: () => void;
		bulkFindPersonIsLoading?: boolean;
	};
}

/**
 * The "Enrich" group for the bulk-action footer: find emails/phones, score, or
 * deep-research the selected prospects. Each item shows its per-prospect credit
 * cost as a subtitle. Extracted from {@link TableFooter} to keep that file under
 * the size cap.
 */
export const FooterEnrichGroup: FunctionComponent<FooterEnrichGroupProps> = ({
	ts,
	isOpen,
	onToggle,
	onClose,
	searchForEmail,
	searchForPhone,
	searchForCompanyEmailAndPhone,
	leadScoring,
	deepResearch,
	findMorePeople,
	bulkFindPerson,
}) => {
	const { t } = useTranslation();
	const isLoading = !!(
		searchForEmail?.searchForEmailIsLoading ||
		searchForPhone?.searchForPhoneIsLoading ||
		searchForCompanyEmailAndPhone?.searchForCompanyEmailAndPhoneIsLoading ||
		leadScoring?.leadScoringIsLoading ||
		deepResearch?.deepResearchIsLoading ||
		findMorePeople?.findMorePeopleIsLoading ||
		bulkFindPerson?.bulkFindPersonIsLoading
	);

	return (
		<FooterPopoverGroup
			dark
			icon={faSparkles}
			isLoading={isLoading}
			isOpen={isOpen}
			label={ts("enrich")}
			loadingLabel={ts("wait")}
			onToggle={onToggle}
		>
			{searchForEmail && (
				<FooterActionItem
					icon={faEnvelope}
					isMenuItem
					label="Search for Email"
					subtitle={creditMenuSubtitle(t, "email")}
					onClick={() => {
						searchForEmail?.searchForEmail?.();
						onClose();
					}}
				/>
			)}
			{searchForPhone && (
				<FooterActionItem
					icon={faPhone}
					isMenuItem
					label="Search for Phone"
					subtitle={creditMenuSubtitle(t, "phone")}
					onClick={() => {
						searchForPhone?.searchForPhone?.();
						onClose();
					}}
				/>
			)}
			{searchForCompanyEmailAndPhone && (
				<FooterActionItem
					icon={faBuilding}
					isMenuItem
					label="Search for Company Email & Phone"
					subtitle={creditMenuSubtitle(t, "companyContact")}
					onClick={() => {
						searchForCompanyEmailAndPhone?.searchForCompanyEmailAndPhone?.();
						onClose();
					}}
				/>
			)}
			{leadScoring && (
				<FooterActionItem
					icon={faStar}
					isMenuItem
					label="Lead Scoring"
					subtitle={creditMenuSubtitle(t, "rescore")}
					onClick={() => {
						leadScoring?.leadScoring?.();
						onClose();
					}}
				/>
			)}
			{deepResearch && (
				<FooterActionItem
					icon={faSparkles}
					isMenuItem
					label="Deep Research"
					subtitle={creditMenuSubtitle(t, "deepResearch")}
					onClick={() => {
						deepResearch?.deepResearch?.();
						onClose();
					}}
				/>
			)}
			{findMorePeople && (
				<FooterActionItem
					icon={faUserPlus}
					isMenuItem
					label="Find more/different people"
					subtitle={t("defineIcps.findMorePeople.creditSubtitle", {
						credits: FIND_PERSON_COMPANY_CREDITS,
					})}
					onClick={() => {
						findMorePeople?.findMorePeople?.();
						onClose();
					}}
				/>
			)}
			{bulkFindPerson && (
				<FooterActionItem
					icon={faUserMagnifyingGlass}
					isMenuItem
					label="Find person"
					subtitle={t("defineIcps.bulkFindPeople.enrichMenuSubtitle", {
						credits: FIND_PERSON_COMPANY_CREDITS,
					})}
					onClick={() => {
						bulkFindPerson?.bulkFindPerson?.();
						onClose();
					}}
				/>
			)}
		</FooterPopoverGroup>
	);
};
